import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { gtmEvent } from '@dtvgo/gtm-event-report';
import { isLowEndDevice } from '~/utils/platform/isLowEndDevice';
import { TAGS_HASH_AND_EVENTS } from '../../utils/googleTags/tagsHashAndEvents';

const GtmLocalContext = createContext(undefined);

/* Low-end TVs (webOS 3/4): deferring a push by one tick is not enough. Each
   app event costs 3.4-4s of blocked main thread inside gtm.js there (measured
   on webOS 3, most of it core-js polyfills), and a page view lands exactly
   when the user starts navigating the new page: on a cold /live two pushes
   froze the EPG for ~7s and delayed the page's own API calls. Queue them
   instead and send one at a time once the remote has been idle for
   LOW_END_IDLE_MS, so the freeze happens while nobody is pressing keys. An
   event never waits longer than LOW_END_MAX_WAIT_MS, and the order is kept. */
const LOW_END_IDLE_MS = 3000;
const LOW_END_MAX_WAIT_MS = 20_000;
const LOW_END_MIN_GAP_MS = 250;
const lowEndQueue = [];
let lastInputAt = 0;
let lowEndFlushTimer;

function trackInput() {
  lastInputAt = Date.now();
}

function scheduleLowEndFlush() {
  if (lowEndFlushTimer || lowEndQueue.length === 0) return;
  const now = Date.now();
  const idleIn = lastInputAt + LOW_END_IDLE_MS - now;
  const overdueIn = lowEndQueue[0].queuedAt + LOW_END_MAX_WAIT_MS - now;
  lowEndFlushTimer = setTimeout(() => {
    lowEndFlushTimer = undefined;
    const at = Date.now();
    const isIdle = at - lastInputAt >= LOW_END_IDLE_MS;
    if (isIdle || at - lowEndQueue[0].queuedAt >= LOW_END_MAX_WAIT_MS) {
      lowEndQueue.shift().report();
    }
    scheduleLowEndFlush();
  }, Math.max(LOW_END_MIN_GAP_MS, Math.min(idleIn, overdueIn)));
}

function queueLowEndReport(report) {
  if (!lastInputAt) {
    /* First event: start tracking the remote (capture, so nothing can stop it). */
    window.addEventListener('keydown', trackInput, true);
    trackInput();
  }
  lowEndQueue.push({ report, queuedAt: Date.now() });
  scheduleLowEndFlush();
}

const contextDefaultValue = {
  miniEpgLiveTitle: undefined,
  pdpSelectedTabTitle: undefined,
  selectedSeason: undefined,
};

function GtmLocalProvider({ children, commonDimensionsData = {} }) {
  const [state, setState] = useState(contextDefaultValue);

  const gtmChangeItem = useCallback((item) => {
    setState((prevState) => ({ ...prevState, ...item }));
  }, []);

  const commonTaggingEvent = useCallback(({
    category,
    subCategory,
    eventName,
    data = {},
  }) => {
    /* Deferred to its own task: gtm.js processes dataLayer.push SYNCHRONOUSLY,
       and most tagging events fire from keypress/focus handlers — on webOS 3 an
       inline push blocked an EPG filter switch for ~3.9s vs ~0.5s deferred.
       Timers run in order, so event order is preserved. */
    const report = () => {
      gtmEvent({
        hash: TAGS_HASH_AND_EVENTS[category][subCategory][eventName]?.hash,
        eventType: TAGS_HASH_AND_EVENTS[category][subCategory][eventName]?.event,
        dimensions: TAGS_HASH_AND_EVENTS[category][subCategory][eventName]?.dimensions,
        userData: commonDimensionsData,
        data,
      });
    };
    if (isLowEndDevice()) {
      queueLowEndReport(report);
    } else {
      setTimeout(report, 0);
    }
  }, [commonDimensionsData]);

  return (
    <GtmLocalContext.Provider
      value={useMemo(() => ({
        gtmState: state,
        gtmChangeItem,
        commonTaggingEvent,
      }), [state, gtmChangeItem, commonTaggingEvent])}
    >
      {children}
    </GtmLocalContext.Provider>
  );
}

export default GtmLocalProvider;
export const useGtmLocalContext = () => useContext(GtmLocalContext);
export { GtmLocalContext };

GtmLocalProvider.propTypes = {
  children: PropTypes.node.isRequired,
  commonDimensionsData: PropTypes.instanceOf(Object),
};
